import tracking from 'dibs-tracking';
import serverVars from 'server-vars';
import { Context } from '@datadog/browser-core';

const PERF_METRIC_CUSTOM_HYDRATE = 'react-hydrate';
const PERF_METRIC_BROWSER_FCP = 'first-contentful-paint';
const PERF_METRIC_BROWSER_FID = 'first-input';

/**
 * Configuration objects to determine what will be uploaded to Google Analytics
 * and Datadog.
 */
const UPLOAD_TO_DD = [PERF_METRIC_BROWSER_FID];
const UPLOAD_TO_GA = [PERF_METRIC_CUSTOM_HYDRATE, PERF_METRIC_BROWSER_FCP, PERF_METRIC_BROWSER_FID];

function shouldUploadToDataDog(name: string): boolean {
    return UPLOAD_TO_DD.includes(name);
}
function shouldUploadToAnalytics(name: string): boolean {
    return !serverVars.get('enablers.disableGaPerformanceTracking') && UPLOAD_TO_GA.includes(name);
}

/**
 * Use these constants when calling mark or measure.
 */
export const PERF_METRICS = {
    HYDRATE: PERF_METRIC_CUSTOM_HYDRATE,
};

const supportsPerformanceObserver =
    typeof window === 'object' &&
    window.PerformanceObserver &&
    !!window.PerformanceObserver.supportedEntryTypes;

// Know when the entry types we would like to use are not supported.
function isSupportedPerfEntryType(entryTypes: string[]): boolean {
    for (const entryType of entryTypes) {
        if (!window.PerformanceObserver.supportedEntryTypes.includes(entryType)) {
            return false;
        }
    }
    return true;
}

function addDatadogUserAction(name: string, context: Context): void {
    if (window.DD_RUM) {
        window.DD_RUM.addUserAction(name, context);
    }
}

function msToNs(duration: number): number {
    // datadog stores duration in ns and PerformanceEntry gives use ms.
    // to stay consistent we will convert duration to ns.
    return +(duration * 1e6).toFixed(0);
}

export function markPerfMetric(name: string): void {
    if (supportsPerformanceObserver) {
        window.performance.mark(name);
    }
}

export function measurePerfMetric(name: string, startMark?: string, endMark?: string): void {
    if (supportsPerformanceObserver) {
        window.performance.measure(name, startMark, endMark);
    }
}

export function initCustomPerformanceReporting(): void {
    if (!supportsPerformanceObserver) {
        return;
    }

    if (isSupportedPerfEntryType(['measure'])) {
        const customObserver = new PerformanceObserver(list => {
            for (const entry of list.getEntries()) {
                const name = entry.name;
                if (shouldUploadToDataDog(name)) {
                    addDatadogUserAction(name, {
                        duration: msToNs(entry.duration),
                    });
                }

                if (shouldUploadToAnalytics(name)) {
                    tracking.trackUserTiming({
                        metric: name,
                        startTime: entry.startTime,
                        duration: entry.duration,
                    });
                }
            }
        });

        customObserver.observe({
            entryTypes: ['measure'],
        });
    }

    /**
     * Register separate observers for buffered performance metrics, because the `buffered` flag
     * is only available with a single entry type.
     *
     * FID - First Input Delay.
     */
    if (isSupportedPerfEntryType([PERF_METRIC_BROWSER_FID])) {
        const fidObserver = new PerformanceObserver(list => {
            for (const entry of list.getEntries()) {
                // @ts-ignore processingStart on PerformanceEntryList is not in official spec yet.
                const processingStart = entry.processingStart;
                const duration = processingStart - entry.startTime;
                /**
                 * Use entry type instead of name, name contains the user interaction event name
                 * that was used to calculate the FID.
                 */
                if (shouldUploadToDataDog(entry.entryType)) {
                    addDatadogUserAction('First Input Delay', {
                        duration: msToNs(duration),
                    });
                }
                if (shouldUploadToAnalytics(entry.entryType)) {
                    tracking.trackUserTiming({
                        metric: entry.entryType,
                        startTime: processingStart,
                        duration,
                    });
                }

                // after getting 'first-input' entry, we can safely disconnect observer
                fidObserver.disconnect();
            }
        });

        fidObserver.observe({
            type: PERF_METRIC_BROWSER_FID,
            buffered: true,
        });
    }

    /**
     * FCP - First Contentful Paint
     */
    if (isSupportedPerfEntryType(['paint'])) {
        const fcpObserver = new PerformanceObserver(list => {
            for (const entry of list.getEntries()) {
                if (shouldUploadToAnalytics(entry.name)) {
                    tracking.trackUserTiming({
                        metric: entry.name,
                        duration: entry.duration,
                        startTime: entry.startTime,
                    });
                }
                // after getting 'first-contentful-paint' entry, we can safely disconnect observer
                fcpObserver.disconnect();
            }
        });

        fcpObserver.observe({
            type: 'paint',
            buffered: true,
        });
    }
}
